export default function Cart() {
    return (
        <div id="cart" className="my-5">
            <h4>Cart JSON Structure</h4>
            <p>
                This document describes each key in the <strong>Cart JSON object</strong> returned from the backend.
                It helps you understand what data fields are available for use inside Handlebars templates or API integrations.
            </p>

            <hr />

            <h5>Root-Level Keys</h5>
            <ul>
                <li><code>customer_id</code> — Unique identifier of the customer who owns this cart.</li>
                <li><code>cart_id</code> — Unique identifier for the cart record.</li>
                <li><code>tax</code> — Total tax amount applied to the cart.</li>
                <li><code>discount</code> — Total discount amount applied across items.</li>
                <li><code>sub_total</code> — Cart subtotal before tax and delivery fees.</li>
                <li><code>total</code> — Final payable amount (after discounts, tax, and delivery fees).</li>
                <li><code>delivery_fee</code> — Shipping or delivery charge for the cart.</li>
                <li><code>gift_card_id</code> — ID of any gift card applied to the cart.</li>
                <li><code>created_at</code> — ISO timestamp when the cart was created.</li>
                <li><code>updated_at</code> — ISO timestamp of the most recent cart update.</li>
                <li><code>discount_code</code> — Discount code details (null if not applied).</li>
                <li><code>items</code> — Array of all line items (products) inside the cart.</li>
            </ul>

            <hr />

            <h5>items (Array)</h5>
            <p>
                Each entry represents a single product added to the customer’s cart.
            </p>
            <ul>
                <li><code>cart_item_id</code> — Unique identifier for the specific cart item record.</li>
                <li><code>cart_id</code> — The parent cart’s ID that this item belongs to.</li>
                <li><code>product_variant_id</code> — ID of the selected product variant.</li>
                <li><code>quantity</code> — Number of units for this product in the cart.</li>
                <li><code>tax</code> — Tax amount applied to this specific item.</li>
                <li><code>deliver_date</code> — Scheduled delivery date (if applicable).</li>
                <li><code>price_after_discount</code> — Price per item after applying discounts.</li>
                <li><code>discount_per_item</code> — Discount value applied per unit.</li>
                <li><code>metadata</code> — Any extra or custom metadata associated with the item.</li>
                <li><code>is_gift_card</code> — Boolean indicating if the item is a gift card.</li>
                <li><code>created_at</code> — Timestamp when the item was added to the cart.</li>
                <li><code>updated_at</code> — Timestamp when the item was last updated.</li>
                <li><code>product_variant</code> — Nested object containing variant-specific details.</li>
                <li><code>discount_code</code> — Discount code data applied to this item (if any).</li>
                <li><code>image</code> — Image URL for this cart item.</li>
            </ul>

            <hr />

            <h5>product_variant (Nested Object)</h5>
            <p>
                Contains detailed information about the selected variant of the product in the cart.
            </p>
            <ul>
                <li><code>product_id</code> — Parent product ID associated with this variant.</li>
                <li><code>image_id</code> — Linked image ID for this variant.</li>
                <li><code>barcode</code> — Variant barcode used for inventory or POS systems.</li>
                <li><code>compare_at_price</code> — Original price before discounts.</li>
                <li><code>inventory_quantity</code> — Available stock quantity.</li>
                <li><code>option1 / option2 / option3</code> — Variant option values (e.g., color, size).</li>
                <li><code>out_of_stock</code> — Boolean indicating if variant is unavailable.</li>
                <li><code>price</code> — Selling price for this variant.</li>
                <li><code>sku</code> — Stock Keeping Unit code.</li>
                <li><code>taxable</code> — Boolean indicating if the variant is taxable.</li>
                <li><code>title</code> — Variant display title (e.g., “White”).</li>
                <li><code>weight</code> — Variant’s weight value.</li>
                <li><code>weight_unit</code> — Weight measurement unit (e.g., kg, g).</li>
                <li><code>metafields</code> — Variant-level metadata (if any).</li>
                <li><code>variant_id</code> — Unique identifier for this variant record.</li>
                <li><code>location_id</code> — Warehouse or inventory location ID.</li>
                <li><code>is_tax_applicable</code> — Boolean indicating if tax applies to this variant.</li>
                <li><code>inventory_tracked</code> — Boolean indicating if variant inventory is tracked.</li>
                <li><code>created_at</code> — Timestamp of variant creation.</li>
                <li><code>updated_at</code> — Timestamp of last variant update.</li>
                <li><code>Image</code> — Object containing variant image details.</li>
                <li><code>inventory</code> — Additional inventory data (if available).</li>
                <li><code>Product</code> — Object containing parent product details.</li>
            </ul>

            <hr />

            <h5>presentment_prices (Nested Object)</h5>
            <ul>
                <li><code>price.amount</code> — Price value as string.</li>
                <li><code>price.currency_code</code> — Currency code (e.g., <code>IND</code>).</li>
            </ul>

            <h5>Image (Nested Object)</h5>
            <ul>
                <li><code>id</code> — Image ID.</li>
                <li><code>src</code> — CDN URL of the variant image.</li>
            </ul>

            <h5>Product (Nested Object)</h5>
            <ul>
                <li><code>product_id</code> — Unique identifier for the product.</li>
                <li><code>title</code> — Product name or title.</li>
                <li><code>status</code> — Indicates if the product is <code>Active</code> or <code>Draft</code>.</li>
                <li><code>handle</code> — URL slug or handle for product page.</li>
                <li><code>images</code> — Array of image objects for this product.</li>
            </ul>

            <h5>Product.images (Array)</h5>
            <ul>
                <li><code>id</code> — Image ID of the product.</li>
                <li><code>src</code> — Image URL for the product.</li>
                <li><code>position</code> — Display order of this image.</li>
            </ul>

            <hr />

            <h5>Example Handlebars Usage</h5>
            <pre><code className="language-hbs">
                {`{{#each cart.items}}
  <div>
    <img src="{{image}}" alt="{{product_variant.title}}" />
    <h3>{{product_variant.Product.title}}</h3>
    <p>Variant: {{product_variant.title}} | Price: ₹{{product_variant.price}}</p>
  </div>
{{/each}}`}
            </code></pre>

            <p>
                You can access cart data using Handlebars by looping through <code>{'{{ "cart.items"}}'}</code> and referencing nested objects directly.
            </p>
        </div>
    )
}
